import React, { useEffect, useMemo, useState } from 'react'
import {
  Form,
  Button,
  Modal,
  Input,
  Icon,
  Tooltip,
  Card,
  Row,
  Switch,
  Col,
  Radio,
  Alert,
  Select
} from 'antd'
import { CreatableSelect } from '../../../../../../../../../components'
import { api } from '../../../../../../../../../services'
import actions from '../../../../../../../../../store/actions'
import { useDispatch, useSelector } from 'react-redux'
import nanoid from 'nanoid'

import options from './storyFormOptions'
import { useTranslation } from 'react-i18next'
import { config } from '../../../../../../../../../constants'
import { getToken } from '../../../../../../../../../services/api/user'

const { Option } = Select

const StoryCreationForm = Form.create()(
  ({
    form,
    previewData = [],
    onCancel,
    lessonUpdate,
    pages,
    lessonTitle,
    currentPage,
    backgroundImageId = '',
    backgroundImageLink: backgroundImageContent = '',
    backgroundImageOpacity = 0,
    backgroundColor = '',
    courseId,
    lessonId,
    orgId,
    initialStory,
    language
  }) => {
    const dispatch = useDispatch()
    const { getFieldDecorator } = form

    const [isGeneratingStory, setIsGeneratingStory] = useState(false)
    const [isSavingStory, setIsSavingStory] = useState(false)
    const [story, setStory] = useState(null)
    const [storyElements, setStoryElements] = useState(null)
    const [isStoryVisible, setIsStoryVisible] = useState(false)
    const [saveOriginalContent, setSaveOriginalContent] = useState(true)
    const [storyPosition, setStoryPosition] = useState('start')
    // Mode state: 'form' or 'chat'
    const [generationMode, setGenerationMode] = useState('form')
    // Chat mode state
    const [chatMessages, setChatMessages] = useState([])
    const [chatInput, setChatInput] = useState('')
    const [isChatLoading, setIsChatLoading] = useState(false)
    const [expandedStoryId, setExpandedStoryId] = useState(null)
    const chatContainerRef = React.useRef(null)
    const { handleLessonInput } = actions.lessonBuilder

    const transformStoryElements = vals => {
      if (!vals) return null
      const hasValues = Object.values(vals).some(v => {
        if (v === undefined || v === null || v === '') return false
        if (Array.isArray(v) && v.length === 0) return false
        return true
      })
      if (!hasValues) return null

      const result = { ...vals }
      const names = Array.isArray(vals.characterNames)
        ? vals.characterNames
        : vals.characterNames
        ? [vals.characterNames]
        : []
      result.character_names = names
      delete result.characterNames
      return result
    }

    const { t } = useTranslation()

    // Reset chat state when switching to chat mode
    useEffect(() => {
      if (generationMode === 'chat') {
        setChatMessages([
          {
            type: 'assistant',
            content: t('v5:story_chat_initial_message'),
            timestamp: new Date(),
            id: Date.now(),
            isStreaming: false
          }
        ])
        setChatInput('')
        setExpandedStoryId(null)
      }
    }, [generationMode, t])
    // If a continued story was passed in, prefill and open editor
    useEffect(() => {
      if (typeof initialStory === 'string' && initialStory.length > 0) {
        setStory(initialStory)
        setIsStoryVisible(true)
      }
    }, [])

    const user = useSelector(state => state.user)

    const handleSubmit = e => {
      e.preventDefault()
      form.validateFields((err, values) => {
        if (!err) {
          const storyElementsObj = transformStoryElements(values)
          setStoryElements(storyElementsObj)
          const contentFromPage = extractSelectedPageText()
          const referenceRemovedContent = removeReferenceList(contentFromPage)
          setIsGeneratingStory(true)
          api.courses
            .continueStory({
              course_content:
                `Course Name : ${lessonTitle} ` + referenceRemovedContent,
              story_elements: storyElementsObj,
              is_continuation: false,
              language
            })
            .then(res => {
              if (res.data) {
                setStory(res.data)
                setIsStoryVisible(true)
              }
            })
            .catch(err => {
              console.log(err)
            })
            .finally(() => setIsGeneratingStory(false))
        }
      })
    }

    const handleSaveStory = async () => {
      try {
        setIsSavingStory(true)
        const contentFromPage = extractSelectedPageText()
        const referenceRemovedContent = removeReferenceList(contentFromPage)
        const currentValues = form.getFieldsValue()
        const storyElementsObj =
          storyElements || transformStoryElements(currentValues)

        const res = await api.courses.saveStory({
          course_id: courseId,
          lesson_id: lessonId,
          org_id: user?.info?.active_organization_id,
          params: {
            course_content: referenceRemovedContent,
            story_elements: storyElementsObj
          },
          story: story
        })
        console.log(res)
      } catch (error) {
        console.log(error)
      } finally {
        setIsSavingStory(false)
      }
    }

    function removeReferenceList(text) {
      if (!text) return ''
      var pattern = /References:[^]*/gs
      var textWithoutReferences = text.replace(pattern, '').trim()
      return textWithoutReferences
    }

    function extractSelectedPageText() {
      const selectedPages = previewData.filter(page => page.pageSelected)

      let concatenatedText = ''

      selectedPages.forEach(page => {
        page.data.forEach(element => {
          if (element.type === 'TEXT' && element.display === 'CONTENT') {
            const content = (element.params.content || '')
              .replace(/<[^>]*>?/gm, '')
              .replace(/I could not find content related to this topic/g, '')
              .trim()

            if (content) {
              concatenatedText += content + ' '
            }
          }
        })
      })

      return concatenatedText.trim()
    }

    function splitLongString(text) {
      let sentences = text.split('. ')

      let sections = []
      let currentSection = ''
      let sectionWordCount = 0

      for (let sentence of sentences) {
        if (sectionWordCount + sentence.split(' ').length <= 150) {
          currentSection += sentence + '. '
          sectionWordCount += sentence.split(' ').length
        } else {
          sections.push(currentSection.trim())
          currentSection = sentence + '. '
          sectionWordCount = sentence.split(' ').length
        }
      }

      if (currentSection) {
        sections.push(currentSection.trim())
      }

      return sections
    }

    const onAddStory = () => {
      if (saveOriginalContent) {
        onAddStoryBlock()
      } else {
        Modal.confirm({
          title: t('v5:story_add_to_pages_title'),
          content: t('v5:story_add_to_pages_content'),
          onOk() {
            handleAddStory()
          },
          onCancel() {
            console.log('Cancel')
          },
          okText: t('v4:ok'),
          cancelText: t('v4:cancel')
        })
      }
    }

    const onAddStoryBlock = () => {
      const isAddtoStart = storyPosition === 'start'
      const storyInSections = splitLongString(story)

      const { title } = getPageTitle(previewData, 1)

      let orderCount = isAddtoStart ? 0 : previewData.length + 1

      const defaultSize = { width: 1024, height: 768 }
      const defaultOrientation = 'landscape'

      const backgroundItem = backgroundImageId
        ? {
            uid: nanoid(8),
            type: 'BACKGROUND_IMAGE',
            params: {
              content: backgroundImageContent,
              content_id: backgroundImageId,
              opacity: backgroundImageOpacity
            }
          }
        : {
            uid: nanoid(8),
            type: 'BACKGROUND_COLOR',
            display: 'BACKGROUND_COLOR',
            params: {
              content: backgroundColor,
              content_id: null,
              opacity: 1
            }
          }

      const addedPages = storyInSections.map((content, index) => ({
        size: defaultSize,
        orientation: defaultOrientation,
        data: [
          {
            uid: nanoid(8),
            type: 'TEXT',
            display: 'CONTENT',
            params: {
              content,
              x: 57,
              y: 146,
              width: 900,
              height: 353
            }
          },
          {
            uid: nanoid(8),
            type: 'TEXT',
            display: 'HEADING',
            params: {
              content: `<h1 class=\"ql-align-center\"><strong style=\"font-size: 36px;\">${t(
                'v5:story_page_heading',
                { lng: language }
              )} ${index + 1} / ${storyInSections.length}</strong></h1>`,
              x: 57,
              y: 50,
              width: 900,
              height: 67,
              media_format: 'TEXT'
            }
          },
          backgroundItem
        ],
        title,
        ordering: orderCount++
      }))

      const defaultPage = {
        data: [],
        orientation: 'portrait',
        size: defaultSize
      }

      const newPageArray = storyInSections.map(() => ({ ...defaultPage }))

      const newPages = isAddtoStart
        ? [...newPageArray, ...pages]
        : [...pages, ...newPageArray]

      const updatedData = isAddtoStart
        ? [...addedPages, ...previewData]
        : [...previewData, ...addedPages]

      newPages.forEach((page, index) => {
        page.ordering = index
      })

      updatedData.forEach((page, index) => {
        page.ordering = index
      })
      update(newPages, updatedData)
    }

    const update = async (newPages, updatedData) => {
      await handleSaveStory()

      if (currentPage !== 1) {
        await dispatch(handleLessonInput('currentPage', 1))
      }
      await dispatch(handleLessonInput('pages', newPages))
      await dispatch(handleLessonInput('pages_landscape', updatedData))
      await lessonUpdate({ pages: newPages, pages_landScape: updatedData })
      onCancel(updatedData)
    }

    const handleAddStory = () => {
      // split story by 150 words each
      const storyInSections = splitLongString(story)
      let storyCount = 0

      // replace existing content with story on the selected pages
      let updatedData = previewData.map(page => {
        if (page.pageSelected) {
          page.data = page.data.map(element => {
            if (element.type === 'TEXT' && element.display === 'CONTENT') {
              const content = storyInSections[storyCount]
              element.params.content = content

              if (!content) {
                page.isEmpty = true
              }

              storyCount++
            }
            return element
          })
        }
        return page
      })

      let newPages = [...pages]
      let dataRef = [...updatedData]

      // add remaining pages with story
      if (storyCount < storyInSections.length) {
        storyInSections.slice(storyCount).forEach((content, index) => {
          const { title, ordering } = getPageTitle(dataRef, storyCount)
          const backgroundItem = backgroundImageId
            ? {
                uid: nanoid(8),
                type: 'BACKGROUND_IMAGE',
                params: {
                  content: backgroundImageContent,
                  content_id: backgroundImageId,
                  opacity: backgroundImageOpacity
                }
              }
            : {
                uid: nanoid(8),
                type: 'BACKGROUND_COLOR',
                display: 'BACKGROUND_COLOR',
                params: {
                  content: backgroundColor,
                  content_id: null,
                  opacity: 1
                }
              }

          const addedPages = {
            size: {
              width: 1024,
              height: 768
            },
            orientation: 'landscape',
            data: [
              {
                uid: nanoid(8),
                type: 'TEXT',
                display: 'CONTENT',
                params: {
                  content,
                  x: 57,
                  y: 146,
                  width: 900,
                  height: 353
                }
              },
              {
                uid: nanoid(8),
                type: 'TEXT',
                display: 'HEADING',
                params: {
                  content: title,
                  x: 57,
                  y: 50,
                  width: 900,
                  height: 67,
                  media_format: 'TEXT'
                }
              },
              backgroundItem
            ]
          }

          const pageData = {
            data: [],
            orientation: 'portrait',
            size: { width: 1024, height: 768 }
          }

          if (ordering && ordering >= 0 && ordering <= updatedData.length) {
            updatedData.splice(ordering, 0, addedPages)
            newPages.splice(ordering, 0, pageData)
          } else {
            updatedData.push(addedPages)
            newPages.push(pageData)
          }
        })
      }

      newPages = newPages
        .filter((page, index) => {
          const data = updatedData[index]

          if (data && data.isEmpty) {
            return false
          }
          return true
        })
        .map((page, index) => ({ ...page, ordering: index + 1 }))

      updatedData = updatedData
        .filter(page => !page.isEmpty)
        .map((page, index) => ({ ...page, ordering: index + 1 }))

      // sort pages
      updatedData.sort((a, b) => a.ordering - b.ordering)
      newPages.sort((a, b) => a.ordering - b.ordering)

      // add story to pages

      update(newPages, updatedData)
    }

    const getPageTitle = (data, index) => {
      let pageInfo = {
        title: '',
        ordering: 0
      }
      let count = 0

      for (let i = 0; i < data.length; i++) {
        const page = data[i]
        if (page.pageSelected) {
          count++
          if (count === index) {
            if (page.data && page.data.length > 0) {
              const headingElement = page.data.find(
                item => item.type === 'TEXT' && item.display === 'HEADING'
              )

              if (
                headingElement &&
                headingElement.params &&
                headingElement.params.content
              ) {
                return {
                  title: headingElement.params.content,
                  ordering: page.ordering
                }
              }
            }
            return {
              title: '',
              ordering: page.ordering
            }
          }
        }
      }

      return pageInfo
    }

    const onCheckChange = checked => setSaveOriginalContent(checked)

    const extractPreviousChatStory = () => {
      const lastChatMessageWithContent = [...chatMessages]
        .reverse()
        .find(message => message.storyContent)

      const previousChatStory = lastChatMessageWithContent
        ? lastChatMessageWithContent.storyContent?.split('<<END>>')[0]
        : ''

      return previousChatStory
    }

    const handleChatSubmit = async e => {
      e && e.preventDefault()
      if (!chatInput.trim() || isChatLoading) return

      const userMessage = {
        type: 'user',
        content: chatInput.trim(),
        timestamp: new Date(),
        id: Date.now()
      }
      const messageId = Date.now() + 1

      setChatMessages(prev => [
        ...prev,
        userMessage,
        {
          type: 'assistant',
          content: '',
          timestamp: new Date(),
          id: messageId,
          isStreaming: true
        }
      ])

      setChatInput('')
      setIsChatLoading(true)

      try {
        const course_content = extractSelectedPageText()
        const previousChatStory = extractPreviousChatStory()
        const data = {
          ...(!previousChatStory && { course_content }),
          message: userMessage.content,
          ...(previousChatStory && { generated_story: previousChatStory }),
          language
        }

        const token = await getToken()
        const baseURL =
          config.questionAnswerAiUrl + '?service=story-generation-chat'

        const response = await fetch(baseURL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(data)
        })

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let accumulatedText = ''
        let chatText = ''
        let storyText = ''
        let foundDelimiter = false

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          const chunk = decoder.decode(value, { stream: true })
          accumulatedText += chunk

          // Check if we've received the delimiter
          if (accumulatedText.includes('<<END>>')) {
            const parts = accumulatedText.split('<<END>>')
            chatText = parts[0]
            storyText = parts[1] || ''
            foundDelimiter = true

            // Update message with both chat text (complete) and streaming story
            setChatMessages(prev =>
              prev.map(msg =>
                msg.id === messageId
                  ? {
                      ...msg,
                      content: chatText,
                      storyContent: storyText,
                      isStreaming: false,
                      isStreamingStory: done ? false : true
                    }
                  : msg
              )
            )
          } else {
            // Still accumulating chat text, stream it
            setChatMessages(prev =>
              prev.map(msg =>
                msg.id === messageId
                  ? {
                      ...msg,
                      content: accumulatedText,
                      isStreaming: true
                    }
                  : msg
              )
            )
          }
        }

        // Final update to ensure streaming is stopped
        setChatMessages(prev =>
          prev.map(msg =>
            msg.id === messageId
              ? {
                  ...msg,
                  content: foundDelimiter ? chatText : accumulatedText,
                  storyContent: foundDelimiter ? storyText : null,
                  isStreaming: false,
                  isStreamingStory: false
                }
              : msg
          )
        )
      } catch (error) {
        console.error('Chat error:', error)
        setChatMessages(prev =>
          prev.map(msg =>
            msg.id === messageId
              ? {
                  ...msg,
                  content: t('v5:story_chat_error_message'),
                  isStreaming: false
                }
              : msg
          )
        )
      } finally {
        setIsChatLoading(false)
      }
    }

    // Auto-scroll to bottom of chat
    useEffect(() => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop =
          chatContainerRef.current.scrollHeight
      }
    }, [chatMessages])

    const toggleStoryExpansion = messageId => {
      setExpandedStoryId(prev => (prev === messageId ? null : messageId))
    }

    console.log({ chatMessages })

    // Chat mode UI
    const renderChat = () => (
      <div className="chat-container">
        <div
          className="chat-messages"
          ref={chatContainerRef}
          style={{
            height: '350px',
            overflowY: 'auto',
            marginBottom: '16px',
            padding: '12px',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            backgroundColor: '#fafafa'
          }}
        >
          {chatMessages.map(msg => (
            <div
              key={msg.id}
              className={`chat-message-item ${msg.type}`}
              style={{ marginBottom: '16px' }}
            >
              <div
                style={{
                  padding: '12px 16px',
                  borderRadius: '12px',
                  backgroundColor: msg.type === 'user' ? '#5057d5' : '#fff',
                  color: msg.type === 'user' ? 'white' : 'black',
                  marginLeft: msg.type === 'user' ? '20%' : '0',
                  marginRight: msg.type === 'user' ? '0' : '20%',
                  position: 'relative',
                  border:
                    msg.type === 'assistant' ? '1px solid #e8e8e8' : 'none',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                }}
              >
                {/* Show loading dots if assistant message is streaming with no content yet */}
                {msg.type === 'assistant' && msg.isStreaming && !msg.content ? (
                  <div
                    style={{ display: 'flex', gap: '4px', padding: '4px 0' }}
                  >
                    <span
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#5057d5',
                        animation: 'bounce 1.4s infinite ease-in-out both',
                        animationDelay: '0s'
                      }}
                    ></span>
                    <span
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#5057d5',
                        animation: 'bounce 1.4s infinite ease-in-out both',
                        animationDelay: '0.16s'
                      }}
                    ></span>
                    <span
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#5057d5',
                        animation: 'bounce 1.4s infinite ease-in-out both',
                        animationDelay: '0.32s'
                      }}
                    ></span>
                  </div>
                ) : (
                  <>
                    <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>
                      {msg.content}
                    </div>
                    {msg.isStreaming && msg.content && (
                      <span
                        style={{
                          display: 'inline-block',
                          width: '2px',
                          height: '16px',
                          backgroundColor:
                            msg.type === 'user'
                              ? 'rgba(255,255,255,0.8)'
                              : '#5057d5',
                          marginLeft: '4px',
                          animation: 'blink 1s infinite'
                        }}
                      ></span>
                    )}
                    {/* Show expandable story section if storyContent exists or is being streamed */}
                    {msg.type === 'assistant' &&
                      (msg.storyContent || msg.isStreamingStory) && (
                        <div
                          style={{
                            marginTop: '12px',
                            border: '1px solid #d9d9d9',
                            borderRadius: '8px',
                            overflow: 'hidden'
                          }}
                        >
                          <div
                            style={{
                              backgroundColor: '#f5f5f5',
                              padding: '8px 12px',
                              fontWeight: '500',
                              fontSize: '14px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              cursor: 'pointer'
                            }}
                            onClick={() => toggleStoryExpansion(msg.id)}
                          >
                            <span>
                              📖 {t('v5:story_generated_story_label')}
                            </span>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}
                            >
                              {msg.isStreamingStory && (
                                <span
                                  style={{ fontSize: '12px', color: '#5057d5' }}
                                >
                                  {t('v5:story_generating')}
                                </span>
                              )}
                              <Icon
                                type={
                                  expandedStoryId === msg.id ? 'up' : 'down'
                                }
                                style={{ fontSize: '12px', color: '#666' }}
                              />
                            </div>
                          </div>
                          {expandedStoryId === msg.id && (
                            <>
                              <div
                                style={{
                                  padding: '12px',
                                  maxHeight: '300px',
                                  overflowY: 'auto',
                                  backgroundColor: '#fff',
                                  whiteSpace: 'pre-wrap',
                                  lineHeight: '1.6',
                                  fontSize: '14px'
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: msg.storyContent || ''
                                }}
                              />
                              {msg.storyContent &&
                                !msg.isStreaming &&
                                !msg.isStreamingStory && (
                                  <div
                                    style={{
                                      padding: '8px 12px',
                                      backgroundColor: '#f5f5f5',
                                      borderTop: '1px solid #d9d9d9',
                                      textAlign: 'right'
                                    }}
                                  >
                                    <Button
                                      type="primary"
                                      size="small"
                                      onClick={() => {
                                        Modal.confirm({
                                          title: t('v5:story_use_story_title'),
                                          content: t(
                                            'v5:story_use_story_content'
                                          ),
                                          centered: true,
                                          onOk() {
                                            setStory(msg.storyContent)
                                            setIsStoryVisible(true)
                                          },
                                          onCancel() {},
                                          cancelText: t('v4:cancel')
                                        })
                                      }}
                                    >
                                      {t('v5:story_use_story_button')}
                                    </Button>
                                  </div>
                                )}
                            </>
                          )}
                        </div>
                      )}
                  </>
                )}
              </div>
              <div
                style={{
                  fontSize: '11px',
                  color: '#999',
                  marginTop: '4px',
                  textAlign: msg.type === 'user' ? 'right' : 'left'
                }}
              >
                {msg.timestamp &&
                  msg.timestamp.toLocaleTimeString &&
                  msg.timestamp.toLocaleTimeString()}
              </div>
            </div>
          ))}
        </div>

        <Form
          onSubmit={handleChatSubmit}
          style={{ display: 'flex', gap: '8px' }}
        >
          <Input
            value={chatInput}
            onChange={e => setChatInput(e.target.value)}
            placeholder={t('v5:story_chat_placeholder')}
            onPressEnter={handleChatSubmit}
            disabled={isChatLoading}
            style={{ borderRadius: '20px' }}
          />
          <Button
            type="primary"
            htmlType="submit"
            loading={isChatLoading}
            disabled={!chatInput.trim()}
            shape="round"
          >
            {t('v5:story_chat_send')}
          </Button>
        </Form>
      </div>
    )

    return (
      <Modal
        width={800}
        title={isStoryVisible ? t('v4:edit_story') : t('v4:story_preferences')}
        centered
        visible
        onCancel={() => onCancel()}
        footer={null}
        className={`${
          isStoryVisible ? 'edit-story' : 'story-preferences'
        } story-creation-modal story-form-tour`}
      >
        <style>
          {`
            @keyframes bounce {
              0%, 80%, 100% { 
                transform: scale(0);
                opacity: 0.5;
              } 
              40% { 
                transform: scale(1);
                opacity: 1;
              }
            }
            @keyframes blink {
              0%, 100% { opacity: 1; }
              50% { opacity: 0; }
            }
          `}
        </style>

        {/* Mode toggle UI */}
        {!isStoryVisible && (
          <div style={{ marginBottom: '16px' }} className="chat-mode-scenario">
            <Button.Group>
              <Button
                type={generationMode === 'form' ? 'primary' : 'default'}
                onClick={() => setGenerationMode('form')}
              >
                <Icon type="form" /> {t('v5:story_form_mode')}
              </Button>
              <Button
                type={generationMode === 'chat' ? 'primary' : 'default'}
                onClick={() => setGenerationMode('chat')}
              >
                <Icon type="message" /> {t('v5:story_chat_mode')}
              </Button>
            </Button.Group>
          </div>
        )}

        {/* Form mode UI (existing) */}
        {!isStoryVisible && generationMode === 'form' && (
          <Form className="story-course-form" onSubmit={handleSubmit}>
            {Object.entries(options).map(([key, values]) => (
              <Form.Item
                labelAlign="top"
                label={
                  <span className="story-course-form-label">
                    <span>{t(`v5:${key}`)} </span>
                    <Tooltip title={t(values.description)}>
                      <Icon type="info-circle" />
                    </Tooltip>
                  </span>
                }
                key={key}
              >
                {getFieldDecorator(key, {
                  rules: [
                    {
                      required: !values.isOptional,
                      message: `${t('v4:please_input_your')} ${t(`v5:${key}`)}!`
                    }
                  ]
                })(
                  key === 'characterNames' ? (
                    <CreatableSelect
                      fieldKey={key}
                      updateOptionValue={(value, field) =>
                        form.setFieldsValue({ [field]: value })
                      }
                      options={[]}
                      isMulti
                      placeholder={t('v4:add_character_names_and_press_enter')}
                    />
                  ) : values.value && values.value.length > 0 ? (
                    <CreatableSelect
                      fieldKey={key}
                      updateOptionValue={(value, field) =>
                        form.setFieldsValue({ [field]: value })
                      }
                      options={values.value.map(optionKey => t(optionKey))}
                      placeholder={`${t('v4:select_or_type_your')} ${t(
                        `v5:${key}`
                      )}`}
                    />
                  ) : values.value && values.value.length > 0 ? (
                    <CreatableSelect
                      fieldKey={key}
                      updateOptionValue={(value, field) =>
                        form.setFieldsValue({ [field]: value })
                      }
                      options={values.value.map(optionKey => t(optionKey))}
                      placeholder={`${t('v4:select_or_type_your')} ${t(
                        `v5:${key}`
                      )}`}
                    />
                  ) : (
                    <Input.TextArea
                      placeholder={`${t('v4:enter_your')} ${t(`v5:${key}`)}`}
                    />
                  )
                )}
              </Form.Item>
            ))}
            <div className="form-button-center">
              <Form.Item>
                <Button
                  loading={isGeneratingStory}
                  className="shl-primary-btn"
                  htmlType="submit"
                  shape="round"
                  size="large"
                >
                  {t('v3:generate_story')}
                </Button>
              </Form.Item>
            </div>
          </Form>
        )}

        {/* Chat mode UI */}
        {!isStoryVisible && generationMode === 'chat' && renderChat()}

        {isStoryVisible && (
          <StoryEditor
            data={story}
            storyPosition={storyPosition}
            saveOriginalContent={saveOriginalContent}
            onChange={setStory}
            onAddStory={onAddStory}
            onCheckChange={onCheckChange}
            setStoryPosition={setStoryPosition}
            isSavingStory={isSavingStory}
          />
        )}
      </Modal>
    )
  }
)

const StoryEditor = ({
  data,
  saveOriginalContent,
  onChange,
  onAddStory,
  onCheckChange,
  storyPosition,
  setStoryPosition,
  isSavingStory
}) => {
  const [story, setStory] = useState(null)

  const { t } = useTranslation()

  useEffect(() => {
    if (data) {
      setStory(data)
    }
  }, [data])

  const handleChange = value => {
    setStory(value)
    onChange(value)
  }

  return (
    <div className="story-editor">
      <div className="story-editor__part">
        <Input.TextArea
          value={story}
          onChange={e => handleChange(e.target.value)}
          rows={10}
        ></Input.TextArea>
      </div>

      <Card style={{ width: '100%' }}>
        <Row gutter={16}>
          {saveOriginalContent && (
            <Col span={24}>
              <Form.Item
                label={
                  <Tooltip title={t('v4:position_of_the_story_in_the_lesson')}>
                    {t('v3:position_of_story')} {'  '}
                    <Icon type="info-circle" />
                  </Tooltip>
                }
                colon={false}
              >
                <Select
                  defaultValue={storyPosition}
                  onChange={value => setStoryPosition(value)}
                >
                  <Option value="start">{t('v3:start_the_lesson')}</Option>
                  <Option value="end">{t('v3:end_the_lesson')}</Option>
                </Select>
              </Form.Item>
            </Col>
          )}
          <Col span={24}>
            <div className="story-choose">
              <Switch
                defaultChecked={saveOriginalContent}
                onChange={onCheckChange}
              />
              <p>{t('v4:save_previous_content_on_the_pages')}</p>
            </div>
          </Col>

          {!saveOriginalContent && (
            <Col span={24}>
              <Alert
                message={t('v4:the_existing_content_in_the_selected_pages')}
                type="info"
              />
            </Col>
          )}
        </Row>
      </Card>

      <Button
        className="shl-primary-btn"
        shape="round"
        onClick={onAddStory}
        loading={isSavingStory}
      >
        {t('v3:add_story_pages')}
      </Button>
    </div>
  )
}

export default StoryCreationForm
